import {ReactElement, useContext, useMemo, useState} from "react";
import {Field, Hint, Label, Message, Radio, Toggle} from "@zendeskgarden/react-forms";
import {Button} from "@zendeskgarden/react-buttons";
import {Close, Header, Modal, Body as ModalBody} from "@zendeskgarden/react-modals";
import {History} from "@material-ui/icons";
import {toast} from "react-toastify";
import {AuthContext} from "../../../../contexts/AuthContext";
import {
    JobType,
    PayrollCompensationItemType,
    PayrollDefaultsPushMode,
    usePushPayrollDefaultsMutation,
} from "../../../../__generated__/generated_types";
import ConfirmationModal from "../../../../components/confirmation-modal/ConfirmationModal";
import styles from "./PayrollDefaults.module.scss";
import {pushingPayrollDefaultsEnabled} from "../../../../global/feature_checkers";
import {ListJobs, ListJobsDisplayColumn} from "../../jobs/ListJobs";
import {defaultsPushCompensationTypesToLabel, defaultsPushModeToLabel} from "./payrollDefaultsPushUtils";

interface PayrollDefaultsPushProps {
    positionIds: string[];
}

type CompensationItemToggle = {
    label: string;
    type: PayrollCompensationItemType;
};

const COMPENSATION_ITEM_TOGGLES: CompensationItemToggle[] = [
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.Medical), type: PayrollCompensationItemType.Medical},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.Dental), type: PayrollCompensationItemType.Dental},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.Vision), type: PayrollCompensationItemType.Vision},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.Retirement_401KMatch), type: PayrollCompensationItemType.Retirement_401KMatch},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.MedicareTax), type: PayrollCompensationItemType.MedicareTax},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.SocialSecurityTax), type: PayrollCompensationItemType.SocialSecurityTax},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.FederalUnemploymentTax), type: PayrollCompensationItemType.FederalUnemploymentTax},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.StateUnemploymentTax), type: PayrollCompensationItemType.StateUnemploymentTax},
    {label: defaultsPushCompensationTypesToLabel(PayrollCompensationItemType.WorkersCompensation), type: PayrollCompensationItemType.WorkersCompensation},
];

const CONFIRMATION_TEXT = "push defaults";

const PUSH_MODE_DETAILS: Record<PayrollDefaultsPushMode, {description: string | JSX.Element; warning: string | null | JSX.Element}> = {
    [PayrollDefaultsPushMode.Set]: {
        description: <>Selected non-empty defaults replace existing employee compensation where a default is configured. <br/><strong>If no default exists for a type, existing data is removed</strong>!</>,
        warning: <>Existing employee data is removed and replaced where defaults exist. <br/><strong>If default does not exist, existing data is removed</strong>!</>,
    },
    [PayrollDefaultsPushMode.Merge]: {
        description: "Selected non-empty defaults replace matching employee entries and add defaults that do not already exist.",
        warning: null,
    },
    [PayrollDefaultsPushMode.Add]: {
        description: "Selected non-empty defaults are added to existing employee compensation. Existing employee data is not deleted.",
        warning: null,
    },
};

export default function PayrollDefaultsPush(props: PayrollDefaultsPushProps): ReactElement {
    const {positionIds} = props;
    const {budgetYear, user} = useContext(AuthContext);

    const [baseCompensation, setBaseCompensation] = useState(false);
    const [overtimeCompensation, setOvertimeCompensation] = useState(false);
    const [raiseCompensation, setRaiseCompensation] = useState(false);
    const [bonusCompensation, setBonusCompensation] = useState(false);
    const [enabledItemTypes, setEnabledItemTypes] = useState<Set<PayrollCompensationItemType>>(new Set());
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showJobLog, setShowJobLog] = useState(false);
    const [defaultsPushMode, setDefaultsPushMode] = useState(PayrollDefaultsPushMode.Set);
    const [pushing, setPushing] = useState(false);

    const [pushPayrollDefaults] = usePushPayrollDefaultsMutation();

    const pushModeDetails = PUSH_MODE_DETAILS[defaultsPushMode];

    const hasSelection = useMemo(() => {
        return baseCompensation
            || overtimeCompensation
            || raiseCompensation
            || bonusCompensation
            || enabledItemTypes.size > 0;
    }, [baseCompensation, overtimeCompensation, raiseCompensation, bonusCompensation, enabledItemTypes]);

    function toggleItemType(type: PayrollCompensationItemType, enabled: boolean) {
        setEnabledItemTypes(current => {
            const next = new Set(current);
            if(enabled) {
                next.add(type);
            } else {
                next.delete(type);
            }
            return next;
        });
    }

    async function pushDefaults() {
        if(positionIds.length === 0 || !hasSelection) {
            return;
        }

        setShowConfirmation(false);
        setPushing(true);
        try {
            await pushPayrollDefaults({
                variables: {
                    budgetYear,
                    positionIds,
                    baseCompensation,
                    overtimeCompensation,
                    raiseCompensation,
                    bonusCompensation,
                    itemCompensationTypes: Array.from(enabledItemTypes),
                    defaultsPushMode,
                },
            });
            toast.success("Payroll defaults push job submitted.");
        } catch {
            toast.error("Failed to submit payroll defaults push job.");
        } finally {
            setPushing(false);
        }
    }

    if(!pushingPayrollDefaultsEnabled(user)) {
        return <></>;
    }

    if(positionIds.length === 0) {
        return <div className={styles.positionSelectorMessage}>Select a position to push defaults.</div>;
    }

    return (
        <div className={styles.pushDefaultsContainer}>
            <div className={styles.pushDefaultsJobHistory}>
                <Button onClick={() => setShowJobLog(true)}>
                    <Button.StartIcon><History /></Button.StartIcon>
                    View Job History
                </Button>
            </div>

            <div className={styles.pushDefaultsMode}>
                <div className={styles.subHeader}>Push mode</div>
                <Field className={styles.pushDefaultsToggle}>
                    <Radio
                        checked={defaultsPushMode === PayrollDefaultsPushMode.Set}
                        onChange={() => setDefaultsPushMode(PayrollDefaultsPushMode.Set)}
                    >
                        <Label>{defaultsPushModeToLabel(PayrollDefaultsPushMode.Set)}</Label>
                        <Hint >
                            Replace existing employee data with defaults where defaults exist. <br/><strong>If default does not exist, existing data is removed</strong>!
                        </Hint>
                    </Radio>
                </Field>
                <Field className={styles.pushDefaultsToggle}>
                    <Radio
                        checked={defaultsPushMode === PayrollDefaultsPushMode.Merge}
                        onChange={() => setDefaultsPushMode(PayrollDefaultsPushMode.Merge)}
                    >
                        <Label>{defaultsPushModeToLabel(PayrollDefaultsPushMode.Merge)}</Label>
                        <Hint>
                            Replace matching entries and add defaults that do not match existing data.<br />
                            Example: Replace a raise of $1,000 with a raise of $1,500 where default and existing raise are on May 15th.
                        </Hint>
                    </Radio>
                </Field>
                <Field className={styles.pushDefaultsToggle}>
                    <Radio
                        checked={defaultsPushMode === PayrollDefaultsPushMode.Add}
                        onChange={() => setDefaultsPushMode(PayrollDefaultsPushMode.Add)}
                    >
                        <Label>{defaultsPushModeToLabel(PayrollDefaultsPushMode.Add)}</Label>
                        <Hint>
                            Add defaults alongside existing employee data.
                        </Hint>
                    </Radio>
                </Field>
            </div>
            <p className={styles.pushDefaultsDescription}>
                {pushModeDetails.warning && <Message validation="warning">
                    {pushModeDetails.warning}
                    <br />
                    <br />
                </Message>}
                {pushModeDetails.description}<br />
                <br />
                Choose which types of compensation to push.<br />
            </p>

            <div className={styles.pushDefaultsList}>
                <div className={styles.pushDefaultsToggleGroup}>
                    {COMPENSATION_ITEM_TOGGLES.map(item => (
                        <Field key={item.type} className={styles.pushDefaultsToggle}>
                            <Toggle
                                checked={enabledItemTypes.has(item.type)}
                                onChange={event => toggleItemType(item.type, event.target.checked)}
                            >
                                <Label>{item.label}</Label>
                            </Toggle>
                        </Field>
                    ))}
                </div>

                <div className={styles.pushDefaultsToggleGroup}>
                    <Message validation="warning">
                        These compensation items are individual most of the times.<br/>
                        Please make sure you indeed want salaries and wages to be pushed in bulk.
                    </Message>
                    <Field className={styles.pushDefaultsToggle}>
                        <Toggle
                            checked={baseCompensation}
                            onChange={event => setBaseCompensation(event.target.checked)}
                        >
                            <Label>Base Compensation</Label>
                        </Toggle>
                    </Field>
                    <Field className={styles.pushDefaultsToggle}>
                        <Toggle
                            checked={overtimeCompensation}
                            onChange={event => setOvertimeCompensation(event.target.checked)}
                        >
                            <Label>Overtime</Label>
                        </Toggle>
                    </Field>
                    <Field className={styles.pushDefaultsToggle}>
                        <Toggle
                            checked={raiseCompensation}
                            onChange={event => setRaiseCompensation(event.target.checked)}
                        >
                            <Label>Raises</Label>
                        </Toggle>
                    </Field>
                    <Field className={styles.pushDefaultsToggle}>
                        <Toggle
                            checked={bonusCompensation}
                            onChange={event => setBonusCompensation(event.target.checked)}
                        >
                            <Label>Bonuses</Label>
                        </Toggle>
                    </Field>
                </div>
            </div>

            <div className={styles.pushDefaultsActions}>
                <Button
                    isPrimary
                    disabled={!hasSelection || pushing}
                    onClick={() => setShowConfirmation(true)}
                >
                    Push Defaults
                </Button>
            </div>

            {showConfirmation &&
                <ConfirmationModal
                    onAccept={pushDefaults}
                    onCancel={() => setShowConfirmation(false)}
                    header="Push payroll defaults to employees?"
                    message={`This will push the selected default compensation types to employees in the selected position(s) for budget year ${budgetYear}. This action cannot be undone.`}
                    confirmationText={CONFIRMATION_TEXT}
                />
            }

            {showJobLog &&
                <Modal
                    className={styles.jobLogModal}
                    onClose={() => setShowJobLog(false)}
                >
                    <Header>
                        Payroll Defaults Push Jobs
                    </Header>
                    <ModalBody className={styles.jobLogModalBody}>
                        <ListJobs
                            jobType={JobType.PayrollDefaultsPush}
                            visibleColumns={[
                                ListJobsDisplayColumn.status,
                                ListJobsDisplayColumn.user,
                                ListJobsDisplayColumn.created,
                                ListJobsDisplayColumn.ended,
                                ListJobsDisplayColumn.details,
                                ListJobsDisplayColumn.errorMessage,
                            ]}
                        />
                    </ModalBody>
                    <Close aria-label="Close modal" />
                </Modal>
            }
        </div>
    );
}
